// Pure mapping of ITAM-2614 RFZO failures into friendly, user-facing messages.
// Extracted from React for unit testing.
//
// Step 1 (pharmacist_create) branches on the STRUCTURED error contract first:
// since backend 84e8a36a, duplicate-therapy and missing-lastTherapyDate return
// 400 JSON {errorCode, message} (D2). Status/substring matching is kept only as
// defense for a stale (pre-rebase) deployment.

import { ConsultRfzoValidationErrors } from "./consultRfzoPayload";

export interface MappedRfzoCreateError {
  message: string;
  /** Field-level mapping when the error is attributable to a single field. */
  fieldErrors?: ConsultRfzoValidationErrors;
}

const asText = (data: unknown): string => {
  if (data == null) {
    return "";
  }
  if (typeof data === "string") {
    return data;
  }
  try {
    return JSON.stringify(data);
  } catch {
    return String(data);
  }
};

const readErrorCode = (data: unknown): string | null => {
  if (
    data &&
    typeof data === "object" &&
    typeof (data as { errorCode?: unknown }).errorCode === "string"
  ) {
    return (data as { errorCode: string }).errorCode;
  }
  return null;
};

/**
 * Request fields the form can highlight, mapped from a bean-validation body.
 *
 * ITAM-2716: `lbo`/`LBO` are INERT at the current backend revision and `jmbg` is
 * deliberately absent. ITAM-2732 removed the only identifier constraint
 * annotation, so neither identifier can ever appear in a field-error map; the
 * server reports identifier problems as a bare-string 400 instead (handled
 * below). The LBO entries stay purely as defence against a pre-ITAM-2732
 * deployment. The remaining bean-validation keys (userEmail, marketplaceId,
 * branchMarketplaceId) are intentionally NOT mapped: none of them is an editable
 * control in this modal, so mapping one would select the "Proverite označena
 * polja" banner without marking any field.
 */
const FIELD_ERROR_KEYS: Record<string, keyof ConsultRfzoValidationErrors> = {
  lbo: "lbo",
  LBO: "lbo",
  firstName: "firstName",
  secondName: "secondName",
  lastTherapyDate: "lastTherapyDate",
  items: "items",
};

/**
 * Bean-validation maps are keyed by the full property path, e.g.
 * `RFZOTherapyPharmacistCreateRequest.LBO`, so only the last segment names the
 * field. This normalization exists purely as compatibility for a pre-ITAM-2732
 * deployment that still emits the class-prefixed LBO key.
 */
const fieldKeyFor = (
  field: string
): keyof ConsultRfzoValidationErrors | undefined =>
  FIELD_ERROR_KEYS[field.split(".").pop() || field];

/**
 * Best-effort field mapping from a Spring bean-validation 400. Two shapes are
 * recognized: the flat `{field: message}` map and the `{errors: [{field,
 * defaultMessage}]}` list. Unrecognized shapes/fields yield null so the caller
 * falls back to the generic message.
 */
const readFieldErrors = (
  data: unknown
): ConsultRfzoValidationErrors | undefined => {
  if (!data || typeof data !== "object") {
    return undefined;
  }
  const errors: ConsultRfzoValidationErrors = {};

  const list = (data as { errors?: unknown }).errors;
  if (Array.isArray(list)) {
    for (const entry of list) {
      const field = (entry as { field?: unknown })?.field;
      const message = (entry as { defaultMessage?: unknown })?.defaultMessage;
      if (typeof field === "string" && typeof message === "string") {
        const key = fieldKeyFor(field);
        if (key) {
          errors[key] = message;
        }
      }
    }
  } else {
    for (const [field, message] of Object.entries(
      data as Record<string, unknown>
    )) {
      if (typeof message !== "string") {
        continue;
      }
      const key = fieldKeyFor(field);
      if (key) {
        errors[key] = message;
      }
    }
  }

  return Object.keys(errors).length > 0 ? errors : undefined;
};

export const mapRfzoCreateError = (
  error: unknown,
  context: { lbo: string; userEmail: string }
): MappedRfzoCreateError => {
  const err = error as {
    response?: { status?: number; data?: unknown };
    message?: string;
  };
  const status = err?.response?.status;
  const data = err?.response?.data;
  const text = asText(data);
  const errorCode = readErrorCode(data);

  // ITAM-2716: the LBO is short and already visible in the form, so naming it
  // helps the pharmacist confirm they typed the right patient. A JMBG-only draft
  // gets the neutral wording instead: the JMBG is a national identifier and is
  // never echoed back, and the backend no longer includes any identifier value
  // in this message either.
  const duplicateMessage = context.lbo
    ? `Za ovog korisnika (LBO ${context.lbo}) već postoji RFZO terapija u ovoj apoteci. Nova terapija ne može da se kreira.`
    : "Za ovog korisnika već postoji RFZO terapija u ovoj apoteci. Nova terapija ne može da se kreira.";

  if (errorCode === "RFZO_THERAPY_ALREADY_EXISTS") {
    // Honest dead end — no recovery affordance (scope decision; plan Section 9).
    return { message: duplicateMessage };
  }
  if (errorCode === "RFZO_LAST_THERAPY_DATE_REQUIRED") {
    // Should be unreachable (FE validates first) — still mapped to the field.
    const message =
      "Poslednji mesec dospeća je obavezan kada je izabran datum dospeća.";
    return { message, fieldErrors: { lastTherapyDate: message } };
  }

  if (status === 404 && /not found in apoteka\.rs/i.test(text)) {
    return {
      message: `Korisnik sa e-mail adresom ${context.userEmail} nije pronađen u apoteka.rs.`,
    };
  }

  // Defensive fallback for a pre-84e8a36a deployment (raw 500 with a plain
  // "already exists" string body).
  if (status != null && status >= 500 && /already exists/i.test(text)) {
    return { message: duplicateMessage };
  }

  if (status === 400) {
    const fieldErrors = readFieldErrors(data);
    if (fieldErrors) {
      return {
        message: "Neispravni podaci terapije. Proverite označena polja.",
        fieldErrors,
      };
    }
    // ITAM-2716: the identifier rejections are a bare Serbian sentence, not
    // JSON: InvalidRequestException returns ex.getMessage() and
    // StringHttpMessageConverter outranks Jackson for a String body. There is no
    // errorCode and no field key, so the text is the only information the server
    // sent. Both messages are already user-appropriate and carry no identifier
    // value, so they are surfaced verbatim rather than discarded.
    if (typeof data === "string" && data.trim()) {
      return { message: data.trim() };
    }
    return {
      message: "Neispravni podaci terapije. Proverite unos i pokušajte ponovo.",
    };
  }

  if (status != null && status >= 500) {
    return { message: "Došlo je do greške na serveru. Pokušajte ponovo." };
  }

  return {
    message:
      err?.message || "Neuspešno kreiranje RFZO terapije. Pokušajte ponovo.",
  };
};

/**
 * Step 2 (order creation). NOTE: no 409-as-success semantics here — that is
 * CnC-specific. A 404 means the therapy row no longer exists, which after a
 * lost success response means the order was ALREADY created (step 2 deletes the
 * row); the board's 10 s poll will show it in Primljene. No automatic
 * reconciliation (scope decision).
 */
export const mapRfzoOrderError = (error: unknown): { message: string } => {
  const err = error as {
    response?: { status?: number; data?: unknown };
    message?: string;
  };
  const status = err?.response?.status;
  const text = asText(err?.response?.data);

  if (status === 404) {
    // Two distinct 404s (verified in apoteka-back @ 84e8a36a):
    //   "RFZOTherapy with id X not found."      -> the row was consumed: the
    //      order almost certainly WAS created (step 2 deletes the therapy on
    //      success, so a retry after a lost response lands here);
    //   "Service item with id X not found for marketplace." -> the CASH
    //      delivery fee item could not be resolved. The method is
    //      @Transactional, so NOTHING was created and the therapy survives —
    //      telling the pharmacist the order exists would be a false success.
    if (/service item/i.test(text)) {
      return {
        message:
          "Usluga rezervacije nije dostupna u vašoj apoteci — rezervacija nije kreirana. Izaberite preuzimanje u apoteci ili kontaktirajte podršku.",
      };
    }
    if (/rfzotherapy|not found/i.test(text) || !text) {
      return {
        message:
          "Terapija više ne postoji — rezervacija je najverovatnije već kreirana. Proverite kolonu Primljene.",
      };
    }
    return { message: "Došlo je do greške. Pokušajte ponovo." };
  }
  if (status === 403) {
    // Authorization regression only (the pharmacist role landed in 84e8a36a);
    // the bare-axios call guarantees this can never log the pharmacist out.
    return {
      message:
        "Nemate ovlašćenje za kreiranje RFZO rezervacije — kontaktirajte podršku.",
    };
  }
  if (status === 400) {
    // Step 2 runs the same post-creation pipeline as a CnC order, so an
    // out-of-zone delivery address arrives here as a 400 too — and it is the
    // NORMAL failure for an out-of-zone RFZO delivery, because the zone
    // pre-check 403s for this role and validation degrades to the backend.
    if (/DeliveryLocationNotInArea|not allowed|not in area|zone|area/i.test(text)) {
      return {
        message: "Adresa je van zone pomoći pri preuzimanju za izabrani tip.",
      };
    }
    // With the FE stock guard this is a race fallback (zero eligible items).
    return {
      message:
        "Nijedan artikal iz terapije trenutno nije dostupan na stanju apoteke — rezervacija nije kreirana.",
    };
  }
  // The backend's zero-stock 400 cannot be relied on (handler-signature bug in
  // 84e8a36a — plan Section 6): it most likely surfaces as a 5xx. Generic error
  // copy, NEVER treated as success.
  if (status != null && status >= 500) {
    return { message: "Došlo je do greške na serveru. Pokušajte ponovo." };
  }

  return {
    message:
      err?.message || "Neuspešno kreiranje RFZO rezervacije. Pokušajte ponovo.",
  };
};
